import { useQuery } from '@tanstack/react-query'
import { comprasDivididasApi, contasApi } from '../../api/endpoints'
import { Input, Select } from '../../components/ui/Input'
import { ModalidadeCartao } from '../../lib/enums'
import { formatMoney } from '../../lib/utils'
import { type PagamentoForm } from './pagamentoUtils'


export function PagamentoVeiculoForm({ valor, onChange, comParcelas = false, valorTotal = 0 }: {
  valor: PagamentoForm; onChange: (p: PagamentoForm) => void; comParcelas?: boolean; valorTotal?: number
}) {
  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list() })
  const { data: cartoes = [] } = useQuery({ queryKey: ['cartoes-utilizaveis'], queryFn: comprasDivididasApi.cartoesUtilizaveis })
  const mudar = (p: Partial<PagamentoForm>) => onChange({ ...valor, ...p })
  const cartao = cartoes.find((c) => c.id === valor.cartaoId)
  const credito = cartao?.modalidade === ModalidadeCartao.Credito
  const aVista = !!cartao && (!credito || valor.debito)
  const meus = cartoes.filter((c) => c.ehMeu)
  const donos = [...new Set(cartoes.filter((c) => !c.ehMeu).map((c) => c.donoNome))]
  const parcelas = Math.max(1, Math.floor(Number(valor.numParcelas) || 1))

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {([['cartao', 'Cartão', 'ri-bank-card-line'], ['conta', 'Conta', 'ri-bank-line']] as const).map(([m, rotulo, icone]) => (
          <button key={m} type="button" onClick={() => mudar({ meio: m })}
            className={`inline-flex h-[38px] flex-1 items-center justify-center gap-2 rounded text-13 font-medium ${valor.meio === m ? 'bg-primary/15 text-primary' : 'bg-surface2 text-muted hover:text-white'}`}>
            <i className={icone} aria-hidden /> {rotulo}
          </button>
        ))}
      </div>
      {valor.meio === 'conta' ? (
        <Select label="Conta" value={valor.accountId} onChange={(e) => mudar({ accountId: e.target.value })}>
          <option value="">Selecione...</option>
          {contas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      ) : (
        <>
          <Select label="Cartão" value={valor.cartaoId} onChange={(e) => mudar({ cartaoId: e.target.value, debito: false, numParcelas: '1' })}>
            <option value="">Selecione...</option>
            {meus.length > 0 && <optgroup label="Seus cartões">{meus.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}</optgroup>}
            {donos.map((d) => <optgroup key={d} label={`Cartões de ${d}`}>{cartoes.filter((c) => !c.ehMeu && c.donoNome === d).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}</optgroup>)}
          </Select>
          {cartao?.temDebito && credito && (
            <label className="flex items-center gap-2 text-13 text-white">
              <input type="checkbox" checked={valor.debito} onChange={(e) => mudar({ debito: e.target.checked, numParcelas: '1' })} /> Paguei no débito deste cartão
            </label>
          )}
          {cartao && (
            <p className="text-2xs text-muted">
              {cartao.ehMeu ? 'Seu cartão' : `Cartão de ${cartao.donoNome}`} · {aVista ? 'sai da conta na hora' : 'crédito, entra na fatura'}
              {!aVista && cartao.limiteDisponivel != null ? ` · limite ${formatMoney(cartao.limiteDisponivel)}` : ''}
            </p>
          )}
        </>
      )}
      {comParcelas && (valor.meio === 'conta' || (cartao && !aVista && cartao.aceitaParcelas)) && (
        <div>
          <Input label="Parcelas" type="number" min={1} max={60} value={valor.numParcelas} onChange={(e) => mudar({ numParcelas: e.target.value })} />
          <p className="mt-1 text-2xs text-muted">
            {valor.meio === 'conta' ? 'Um lançamento por mês.' : 'Parcelas na fatura.'}
            {parcelas > 1 && valorTotal > 0 ? ` ${parcelas}x de ${formatMoney(valorTotal / parcelas)}.` : ''}
          </p>
        </div>
      )}
    </div>
  )
}
